/*
 * Optimizely Feature Experimentation — shared shopper attribute helpers.
 *
 * Pure and SDK-free so both the /optimizely-page SSR targeting session and the
 * client-side conversion tracking build identical user contexts; decisions and
 * events must share userId + attributes or Optimizely splits the visitor.
 */

/**
 * Normalize customer group IDs (CSV string or array) into a trimmed,
 * de-duplicated array. Group IDs are case-sensitive, so casing is preserved.
 * @param {string|string[]|*} groups
 * @returns {string[]}
 */
function normalizeCustomerGroups(groups) {
    let list = []
    if (Array.isArray(groups)) list = groups
    else if (typeof groups === 'string') list = groups.split(',')
    const ids = list.map((id) => String(id).trim()).filter(Boolean)
    return [...new Set(ids)]
}

/* Visitor-level keys the browser (or the CDN, for country) supplies. FX has no
 * built-in attributes, so these are sent explicitly on decisions and events. */
const VISITOR_CONTEXT_KEYS = [
    'device',
    'browser',
    'language',
    'locale',
    'referrer',
    'source',
    'campaign',
    'returning_user',
    'country'
]
const MAX_STRING = 200

/**
 * Whitelist + scalar-only copy of a visitor context (client-sent header or
 * local capture). Unknown keys, objects and empty strings are dropped.
 * @param {Object} [input]
 * @returns {Object}
 */
function sanitizeVisitorContext(input) {
    if (!input || typeof input !== 'object') return {}
    const out = {}
    for (const key of VISITOR_CONTEXT_KEYS) {
        const value = input[key]
        if (typeof value === 'boolean') out[key] = value
        else if (typeof value === 'string' && value.trim())
            out[key] = value.trim().slice(0, MAX_STRING)
    }
    return out
}

/** Storefront country from a locale id: en-CA / fr-CA → CA, en-US → US. */
function siteCountryFromLocale(localeId) {
    return (
        String(localeId || '')
            .split('-')[1]
            ?.toUpperCase() || ''
    )
}

/**
 * Build the scalar attribute map sent to Optimizely's user context. Names
 * follow the Web Experimentation project (site_* = storefront, is_logged_in)
 * so audiences read the same across both platforms. Customer groups are
 * exposed as a CSV string AND a per-group boolean `group_<ID>: true`.
 * @param {{brandSlug: string, localeId: string, isLoggedIn: boolean, customerGroups?: string[], visitorContext?: Object}} params
 * @returns {Object} scalar-only attribute map
 */
function buildOptimizelyAttributes({
    brandSlug,
    localeId,
    isLoggedIn,
    customerGroups = [],
    visitorContext
}) {
    const attributes = {
        ...sanitizeVisitorContext(visitorContext),
        brand: brandSlug,
        site_locale: localeId,
        site_country: siteCountryFromLocale(localeId),
        site_language: String(localeId || '')
            .split('-')[0]
            .toLowerCase(),
        is_logged_in: !!isLoggedIn,
        customerGroups: customerGroups.join(',')
    }
    customerGroups.forEach((groupId) => {
        attributes[`group_${groupId}`] = true
    })
    return attributes
}

/**
 * Optimizely attributes and event tags accept only scalar values; arrays and
 * objects are dropped so they never reach the SDK.
 * @param {Object} [values]
 * @returns {Object} entries containing only string/number/boolean/null values
 */
function pruneScalars(values) {
    if (!values || typeof values !== 'object') return {}
    return Object.entries(values).reduce((acc, [key, value]) => {
        if (
            value === null ||
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean'
        ) {
            acc[key] = value
        }
        return acc
    }, {})
}

export {
    normalizeCustomerGroups,
    buildOptimizelyAttributes,
    pruneScalars,
    sanitizeVisitorContext,
    siteCountryFromLocale,
    VISITOR_CONTEXT_KEYS
}
